/**
 * @voltxampere/web — `<vxa-ac-config-dialog>` AC sweep config modal
 * (Sprint 21 C7 — Sprint 20 C6 deferred).
 *
 * Minimal F6 modal with fStart / fStop / points / sweep form.
 * Sprint 22+ will refine the UI (focus trap, drag-drop, preset
 * picker); this commit ships the form contract + i18n + AC run
 * trigger so F6 can override the circuit's defaults without
 * editing simulationSettings.ac directly.
 *
 * Open/close via the `acConfigDialogOpen` signal — main.ts wires
 * F6 to flip it. Form submit calls `runAC(circuit, {sweep})`
 * with the entered overrides.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect, signal } from "@preact/signals-core";

import { currentCircuit } from "../store/circuit.js";
import { runAC } from "../store/ac-runner.js";
import { t } from "../i18n/index.js";

export const acConfigDialogOpen = signal(false);

interface FormState {
  fStart: number;
  fStop: number;
  points: number;
  sweep: "dec" | "oct" | "lin";
}

@customElement("vxa-ac-config-dialog")
export class VxaAcConfigDialog extends LitElement {
  @state() private _open = false;
  @state() private _form: FormState = { fStart: 1, fStop: 1e6, points: 100, sweep: "dec" };
  @state() private _error: string | null = null;

  private cleanup: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.cleanup = effect(() => {
      this._open = acConfigDialogOpen.value;
      if (this._open) this.seedFromCircuit();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cleanup?.();
    this.cleanup = null;
  }

  static override styles = css`
    .backdrop {
      position: fixed;
      inset: 0;
      background: rgba(3, 5, 10, 0.7);
      display: grid;
      place-items: center;
      z-index: 1000;
    }
    .panel {
      width: min(420px, 90vw);
      padding: 24px;
      background: rgba(8, 12, 23, 0.96);
      border: 1px solid rgba(180, 180, 200, 0.2);
      border-radius: 8px;
      color: var(--ivory, #f3ead5);
    }
    h2 {
      font-family: var(--font-display, "Cormorant Infant", serif);
      font-size: 22px;
      letter-spacing: 0.02em;
      margin: 0 0 16px;
      color: var(--brass, #c08a3e);
    }
    label {
      display: block;
      font-family: var(--font-mono, monospace);
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      margin-bottom: 4px;
      color: var(--ivory-dim, rgba(220, 220, 230, 0.55));
    }
    input,
    select {
      width: 100%;
      padding: 8px 10px;
      margin-bottom: 12px;
      font-family: var(--font-mono, monospace);
      font-size: 13px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(180, 180, 200, 0.2);
      color: inherit;
      border-radius: 4px;
    }
    input:focus,
    select:focus {
      outline: 1px solid var(--brass, #c08a3e);
    }
    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 16px;
    }
    button {
      padding: 8px 16px;
      font-family: var(--font-mono, monospace);
      font-size: 12px;
      letter-spacing: 0.1em;
      background: transparent;
      border: 1px solid rgba(180, 180, 200, 0.3);
      color: inherit;
      cursor: pointer;
      border-radius: 4px;
    }
    button.primary {
      border-color: var(--brass, #c08a3e);
      color: var(--brass, #c08a3e);
    }
    .error {
      color: #d97565;
      font-size: 11px;
      margin: -4px 0 12px;
      font-family: var(--font-mono, monospace);
    }
  `;

  override render() {
    if (!this._open) return null;
    return html`
      <div
        class="backdrop"
        @click=${(e: MouseEvent) => {
          this.maybeClose(e);
        }}
      >
        <div
          class="panel"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
          }}
          role="dialog"
        >
          <h2>${t("ac.config.title")}</h2>
          ${this._error !== null ? html`<div class="error">${this._error}</div>` : null}
          <label>${t("ac.config.fStart")}</label>
          <input
            type="number"
            min="0"
            step="any"
            .value=${String(this._form.fStart)}
            @input=${(e: Event) => {
              this.setField("fStart", parseFloat((e.target as HTMLInputElement).value));
            }}
          />
          <label>${t("ac.config.fStop")}</label>
          <input
            type="number"
            min="0"
            step="any"
            .value=${String(this._form.fStop)}
            @input=${(e: Event) => {
              this.setField("fStop", parseFloat((e.target as HTMLInputElement).value));
            }}
          />
          <label>${t("ac.config.points")}</label>
          <input
            type="number"
            min="2"
            step="1"
            .value=${String(this._form.points)}
            @input=${(e: Event) => {
              this.setField("points", parseInt((e.target as HTMLInputElement).value, 10));
            }}
          />
          <label>${t("ac.config.sweepLabel")}</label>
          <select
            .value=${this._form.sweep}
            @change=${(e: Event) => {
              this.setField("sweep", (e.target as HTMLSelectElement).value as FormState["sweep"]);
            }}
          >
            <option value="dec">${t("ac.config.sweep.dec")}</option>
            <option value="oct">${t("ac.config.sweep.oct")}</option>
            <option value="lin">${t("ac.config.sweep.lin")}</option>
          </select>
          <div class="actions">
            <button
              type="button"
              title=${t("ac.config.cancel") + " (Esc)"}
              @click=${() => {
                this.close();
              }}
            >
              ${t("ac.config.cancel")}
            </button>
            <button
              class="primary"
              @click=${() => {
                this.runAndClose();
              }}
            >
              ${t("ac.config.run")}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private seedFromCircuit(): void {
    const c = currentCircuit.value;
    if (!c) return;
    const ac = c.simulationSettings.ac;
    this._form = {
      fStart: ac.start,
      fStop: ac.stop,
      points: ac.points,
      sweep: ac.sweep === "lin" ? "lin" : "dec",
    };
    this._error = null;
  }

  private setField<K extends keyof FormState>(key: K, value: FormState[K]): void {
    this._form = { ...this._form, [key]: value };
    this._error = null;
  }

  private maybeClose(e: MouseEvent): void {
    if (e.target === e.currentTarget) this.close();
  }

  private close(): void {
    acConfigDialogOpen.value = false;
  }

  private runAndClose(): void {
    if (!Number.isFinite(this._form.fStart) || this._form.fStart <= 0) {
      this._error = t("ac.errors.fStartInvalid");
      return;
    }
    if (!Number.isFinite(this._form.fStop) || this._form.fStop <= this._form.fStart) {
      this._error = t("ac.errors.fStartGreaterEqStop");
      return;
    }
    if (!Number.isInteger(this._form.points) || this._form.points < 2) {
      this._error = t("ac.errors.pointsInvalid");
      return;
    }
    const circuit = currentCircuit.value;
    if (!circuit) return;
    this.close();
    void runAC(circuit, { sweep: { ...this._form } });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-ac-config-dialog": VxaAcConfigDialog;
  }
}
